import { isArray, isPlainObject } from '@dimjs/lang';
import { classNames } from '@dimjs/utils';
import { LabelValueItem } from '@flatbiz/utils';
import { hooks } from '@wove/react';
import { ReactElement, useMemo, useRef } from 'react';
import { useEffectCustom } from '../hooks';
import { ListItem } from '../list/list-item';
import { ModalDrawer, ModalDrawerProps } from '../modal-drawer';
import { PickerColumnItem, PickerColumnValue } from '../picker';
import './style.less';
import { PickerCascade, PickerCascadeProps } from './view';

export interface ModalUpPickerCascadeProps {
  className?: string;
  visible?: boolean;
  title: string;
  itemHeight?: number;
  dataSource: PickerCascadeProps['dataSource'];
  onChange?: (data: { label: string; value: PickerColumnItem<PickerColumnValue>[] }) => void;
  onClose?: () => void;
  value?: PickerCascadeProps['value'];
  drawerProps?: Omit<ModalDrawerProps, 'visible' | 'className'>;
  header?: ReactElement | string;
  onValueChange?: (selectedData?: LabelValueItem[]) => {};
}

/**
 * 级联选择器
 * ```
 * 1. 可自定义header结构
 * 2. 与Form结合使用赋值与取值方式
 *    例如：
 *    a. 赋值格式1: { label: '三国演义/诸葛亮', value: [{ label: '三国演义', value: '1' }, { label: '诸葛亮', value: '1-1' }]}
 *    a. 赋值格式2: { label: '三国演义/诸葛亮', value: ['1', '1-1']}
 *    b. 取值格式:
 *        {
 *          label: '三国演义/诸葛亮',
 *          value: [{ label: '三国演义', value: '1' }, { label: '诸葛亮', value: '1-1' }]
 *        }
 *       可通过使用form.getFormModalPickerValue(value) 直接取值 => ['1', '1-1']
 * ```
 */
export const ModalUpPickerCascade = (props: ModalUpPickerCascadeProps) => {
  const selectedTarget = useRef<PickerColumnItem[]>([]);

  const valueList = useMemo(() => {
    const value = props.value;
    if (!value || !isArray(value)) return undefined;
    if (value.length == 0) return [];
    return value.map((temp) => {
      return isPlainObject(temp) ? temp['value'] : temp;
    });
  }, [props.value]);

  useEffectCustom(() => {
    const dataSource = props.dataSource;
    if (!valueList) {
      props.onValueChange?.(undefined);
      return;
    }
    if (valueList.length > 0) {
      const target1Index = dataSource.findIndex((temp) => temp.value === valueList[0]);
      const target = dataSource[target1Index];
      const target2Item = target.children?.find((temp) => temp.value === valueList[1]);
      if (target2Item) {
        selectedTarget.current = [
          {
            label: target.label,
            value: target.value,
          },
          target2Item,
        ];
        props.onValueChange?.(selectedTarget.current);
        return;
      }
    }
    selectedTarget.current = [
      {
        label: props.dataSource[0].label,
        value: props.dataSource[0].value,
      },
    ];
    if (props.dataSource[0]?.children) {
      selectedTarget.current.push(props.dataSource[0].children[0]);
    }
    props.onValueChange?.(selectedTarget.current);
  }, [valueList]);

  const onChange: PickerCascadeProps['onChange'] = hooks.useCallbackRef((pickerColumnItem) => {
    selectedTarget.current = pickerColumnItem;
  });

  const onConfirm = hooks.useCallbackRef(() => {
    const target = props.dataSource[0];
    let selectedList = [] as PickerColumnItem[];
    if (selectedTarget.current && selectedTarget.current.length > 0) {
      selectedList = selectedTarget.current;
    } else {
      selectedList = [
        {
          label: target.label,
          value: target.value,
        },
        {
          label: target.children?.[0].label || '',
          value: target.children?.[0].value || '',
        },
      ];
    }
    const respData = {
      label: selectedList.map((temp) => temp?.label).join(''),
      value: selectedList,
    };
    props.onChange?.(respData);
    props.onValueChange?.(selectedList);
  });

  return (
    <ModalDrawer
      closeByMask={true}
      onClose={props.onClose}
      {...props.drawerProps}
      className={classNames('v-modal-up-picker-cascade', props.className)}
      visible={props.visible}
    >
      <div className="vmp-content">
        {props.header ? (
          props.header
        ) : (
          <ListItem
            prefix={
              <span onClick={props.onClose} className="vmpc-content-cancel">
                取消
              </span>
            }
            extra={
              <span onClick={onConfirm} className="vmpc-content-confirm">
                确定
              </span>
            }
          >
            {props.title}
          </ListItem>
        )}
        <div className="vmpc-list">
          <PickerCascade dataSource={props.dataSource} onChange={onChange} value={valueList} />
        </div>
      </div>
    </ModalDrawer>
  );
};
